import React from 'react';
import {
  relativeDay, fmtDuration, dayProgress, tasksOnDay, shelvedTasks, trashDaysLeft, addDays,
} from '../myDayUtils';

const MOODS = [
  { value: 1, icon: '😖', label: 'Rough' },
  { value: 2, icon: '😕', label: 'Below par' },
  { value: 3, icon: '😐', label: 'Fine' },
  { value: 4, icon: '🙂', label: 'Good' },
  { value: 5, icon: '🤩', label: 'Great' },
];

/**
 * The shutdown ritual: look at what actually happened, decide what to do with
 * what slipped, and write the day off deliberately instead of just closing the
 * laptop on it.
 */
export default function ReviewView({
  tasks, date, today, days, dayMeta, onDayMeta, onToggle, onAction, onCloseDay, onRestore,
  trash, onRestoreTrash, onPurgeTrash, onEmptyTrash,
}) {
  const onDay = tasksOnDay(tasks, date);
  const done = onDay.filter(t => t.done);
  const slipped = onDay.filter(t => !t.done);
  const progress = dayProgress(tasks, date, days);
  const focusMin = Math.round(onDay.reduce((s, t) => s + (t.focusSec || 0), 0) / 60);
  const review = dayMeta.review || {};
  const shelved = shelvedTasks(tasks);
  const setReview = patch => onDayMeta({ review: { ...review, ...patch } });

  return (
    <div className="md-review">
      <p className="md-lead">
        Closing out <strong>{relativeDay(date, today)}</strong>. Decide on each loose end
        here and the rest of the app stays honest about what's really outstanding.
      </p>

      <div className="md-tiles md-tiles--sm">
        <div className="md-tile"><span className="md-tile__lbl">Completed</span><span className="md-tile__val">{progress.done}</span><span className="md-tile__unit">of {progress.total}</span></div>
        <div className="md-tile"><span className="md-tile__lbl">Follow-through</span><span className="md-tile__val">{progress.pct}%</span><span className="md-tile__unit">of the plan</span></div>
        <div className="md-tile"><span className="md-tile__lbl">Focus logged</span><span className="md-tile__val">{fmtDuration(focusMin)}</span><span className="md-tile__unit">today</span></div>
      </div>

      <section className="md-card">
        <h3 className="md-card__title">✅ Done — {done.length}</h3>
        {done.length === 0 ? (
          <p className="md-empty md-empty--sm">Nothing ticked off yet.</p>
        ) : (
          <ul className="md-review__done">
            {done.map(t => (
              <li key={t.id}>
                <button className="md-link" onClick={() => onToggle(t)} title="Put it back on the list">↺</button>
                <span>{t.title}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="md-card">
        <h3 className="md-card__title">↪ Still open — {slipped.length}</h3>
        {slipped.length === 0 ? (
          <p className="md-empty md-empty--sm">Clean sweep. Nothing to carry.</p>
        ) : (
          <>
            <p className="md-card__sub">
              Anything you leave here rolls onto tomorrow by itself. Park it to freeze it
              where it is, or drop it if it was never really going to happen.
            </p>
            <ul className="md-review__open">
              {slipped.map(t => (
                <li key={t.id} className="md-review__item">
                  <button className="md-check" onClick={() => onToggle(t)} aria-label={`Mark ${t.title} done`}>
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="3.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </button>
                  <span className="md-review__title">
                    {t.title}
                    {t.carryCount > 0 && <em className="md-meta md-meta--carry">↪ ×{t.carryCount}</em>}
                  </span>
                  <span className="md-review__acts">
                    <button className="md-btn md-btn--sm" onClick={() => onAction('tomorrow', t)}>→ Tomorrow</button>
                    <button className="md-btn md-btn--sm" onClick={() => onAction('nextweek', t)}>→ Next week</button>
                    <button className={`md-btn md-btn--sm${t.noCarry ? ' md-btn--on' : ''}`}
                      onClick={() => onAction('park', t)}
                      title="Stop it rolling forward automatically">
                      {t.noCarry ? '⏸ Parked' : '⏸ Park'}
                    </button>
                    <button className="md-btn md-btn--sm md-btn--danger" onClick={() => onAction('delete', t)}>Drop</button>
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <section className="md-card">
        <h3 className="md-card__title">📝 Reflection</h3>
        <label className="md-field">
          <span className="md-field__lbl">What went well</span>
          <textarea className="md-input" rows={3} value={review.wins || ''}
            placeholder="Lunch rush handled without a queue. Chutney prep finally ahead of schedule."
            onChange={e => setReview({ wins: e.target.value })} />
        </label>
        <label className="md-field">
          <span className="md-field__lbl">What got in the way</span>
          <textarea className="md-input" rows={3} value={review.blockers || ''}
            placeholder="Propane delivery late again — chase a second supplier."
            onChange={e => setReview({ blockers: e.target.value })} />
        </label>
        <div className="md-field">
          <span className="md-field__lbl">How did the day feel</span>
          <div className="md-moods">
            {MOODS.map(m => (
              <button key={m.value}
                className={`md-mood${review.mood === m.value ? ' md-mood--on' : ''}`}
                onClick={() => setReview({ mood: review.mood === m.value ? null : m.value })}
                aria-pressed={review.mood === m.value}
                title={m.label}
              >
                <span aria-hidden="true">{m.icon}</span>
                <span className="md-mood__lbl">{m.label}</span>
              </button>
            ))}
          </div>
        </div>
        <label className="md-field">
          <span className="md-field__lbl">One thing to set up for tomorrow ({relativeDay(addDays(date, 1), today)})</span>
          <textarea className="md-input" rows={2} value={review.tomorrow || ''}
            placeholder="Pull 4kg of dal out to soak overnight."
            onChange={e => setReview({ tomorrow: e.target.value })} />
        </label>
        <div className="md-review__close">
          <button className="md-btn md-btn--primary" onClick={onCloseDay}>
            {dayMeta.closedAt ? '✓ Day closed — update' : '🌙 Close out the day'}
          </button>
          {dayMeta.closedAt && (
            <span className="md-hint">Closed {new Date(dayMeta.closedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
          )}
        </div>
      </section>

      {shelved.length > 0 && (
        <section className="md-card">
          <h3 className="md-card__title">📥 Shelved — {shelved.length}</h3>
          <p className="md-card__sub">
            Work you took off the list without finishing it. Nothing here is carried
            forward or counted; bring one back when it's actually the week for it.
          </p>
          <ul className="md-review__open">
            {shelved.map(t => (
              <li key={t.id} className="md-review__item">
                <span className="md-review__title">
                  {t.title}
                  {t.shelvedAt && (
                    <em className="md-meta">shelved {relativeDay(new Date(t.shelvedAt).toISOString().slice(0, 10), today).toLowerCase()}</em>
                  )}
                </span>
                <span className="md-review__acts">
                  <button className="md-btn md-btn--sm" onClick={() => onRestore(t)}>↩ Back to today</button>
                  <button className="md-btn md-btn--sm md-btn--danger" onClick={() => onAction('delete', t)}>Delete</button>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(trash || []).length > 0 && (
        <section className="md-card">
          <header className="md-card__head">
            <div>
              <h3 className="md-card__title">🗑 Recently deleted — {trash.length}</h3>
              <p className="md-card__sub">
                Deleted work waits here for 30 days before it goes for good, so a mistap
                on a phone is never the end of it.
              </p>
            </div>
            <span className="md-card__actions">
              <button className="md-btn md-btn--sm" onClick={() => onRestoreTrash(trash.map(t => t.id))}>
                ↩ Restore all
              </button>
              <button className="md-btn md-btn--sm md-btn--danger" onClick={onEmptyTrash}>
                Empty trash
              </button>
            </span>
          </header>
          <ul className="md-review__open">
            {trash.map(t => {
              const left = trashDaysLeft(t);
              return (
                <li key={t.id} className="md-review__item">
                  <span className="md-review__title">
                    {t.title || 'Untitled activity'}
                    <em className={`md-meta${left <= 3 ? ' md-meta--late' : ''}`}>
                      {left === 0 ? 'gone today' : `${left} day${left === 1 ? '' : 's'} left`}
                    </em>
                  </span>
                  <span className="md-review__acts">
                    <button className="md-btn md-btn--sm" onClick={() => onRestoreTrash([t.id])}>↩ Restore</button>
                    <button className="md-btn md-btn--sm md-btn--danger" onClick={() => onPurgeTrash([t.id])}>
                      Delete forever
                    </button>
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}
